import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiFetch } from "@/common/lib/api";
import type { Contact, ContactPhone } from "@/common/utils/types";

/** Response of POST phone/contacts/:contactId/phones/refresh (API-Server phoneRoutes.ts, EN-1255). */
export interface ContactPhonesResponse {
    phones: ContactPhone[];
    /** The PIMS's raw string for the number to prefill (same convention as contacts.primary_phone). */
    prefill: string | null;
    source: "live" | "stored";
}

export interface ContactPhonesState extends ContactPhonesResponse {
    /** True once the server answered. Stays false after any failure; the stored data is returned instead. */
    refreshed: boolean;
}

export const CONTACT_PHONES_QUERY_KEY = ["contact-phones"] as const;

/** How long a live lookup is reused per contact; mirrors the API-Server per-contact cooldown. */
export const CONTACT_PHONES_STALE_MS = 30_000;

const refreshContactPhones = async (contactId: string, signal: AbortSignal): Promise<ContactPhonesResponse | null> => {
    const response = await apiFetch<ContactPhonesResponse>(`phone/contacts/${contactId}/phones/refresh`, {
        method: "POST",
        json: {},
        signal,
        // A failed lookup (PIMS down, bridge offline) is expected and the dialer quietly keeps the
        // stored numbers, so no toast and no Sentry event. API-Server logs the cause as a warning.
        silent: true,
    });
    return response ?? null;
};

/**
 * EN-1255: every number the PIMS holds for a contact. Returns the stored `contacts.phones` at once,
 * then the result of a live PIMS lookup (the server writes it back to `contacts`), reused for
 * CONTACT_PHONES_STALE_MS per contact and otherwise made once per mount.
 * Any failure keeps the stored list and stored `primary_phone` prefill, with nothing shown.
 */
export const useContactPhones = (contact: Contact | null | undefined): ContactPhonesState => {
    const contactId = contact?.id ?? null;

    const query = useQuery({
        queryKey: [...CONTACT_PHONES_QUERY_KEY, contactId],
        queryFn: ({ signal }) => refreshContactPhones(contactId as string, signal),
        enabled: Boolean(contactId),
        // Matches the API's 30s per-contact cooldown: a reopen within 30s reuses the last result
        // instead of hitting the PIMS again; after that it refetches on mount. The entry is kept
        // for the same window so a quick reopen can find it.
        staleTime: CONTACT_PHONES_STALE_MS,
        gcTime: CONTACT_PHONES_STALE_MS,
        retry: false,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
    });

    const live = query.data;
    const storedPhones = contact?.phones;
    const storedPrefill = contact?.primary_phone ?? null;

    return useMemo<ContactPhonesState>(() => {
        if (live && Array.isArray(live.phones)) {
            return { phones: live.phones, prefill: live.prefill, source: live.source, refreshed: true };
        }
        return { phones: storedPhones ?? [], prefill: storedPrefill, source: "stored", refreshed: false };
    }, [live, storedPhones, storedPrefill]);
};
